import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Trash2, Pencil, Film, ImageIcon } from "lucide-react";
import { HeaderNav } from "../components/HeaderNav";
import { statusText } from "../components/studio/JobProgress";
import { api, ACTIVE, errDetail, fileUrl, posterUrl } from "../lib/api";
import { useLang } from "../lib/i18n";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const CreationCard = ({ p, onDelete }) => {
  const { t, te } = useLang();
  const cur = p.versions[p.current]?.file;
  const job = p.jobs[0];
  const KindIcon = p.kind === "video" ? Film : ImageIcon;
  return (
    <article className="frz-card" data-testid={`creation-card-${p.id}`}>
      <Link to={`/studio/${p.id}`} className="frz-card-thumb" data-testid={`creation-open-${p.id}`}>
        {cur && <img src={posterUrl(cur)} alt={p.title || ""} loading="lazy" />}
        <span className="frz-card-kind"><KindIcon size={14} /></span>
      </Link>
      <div className="frz-card-body">
        <h3 title={p.title}>{p.title || "—"}</h3>
        <p className="frz-muted frz-small">{t("versions_n", { n: p.versions.length })} · {new Date(p.updated_at || p.created_at).toLocaleString()}</p>
        {job && (
          <p className={`frz-badge is-${job.status}`} data-testid={`creation-status-${p.id}`}>
            {job.label || t(`op_${job.type}`)} · {statusText(job, t)}
          </p>
        )}
        {job?.status === "error" && <p className="frz-error frz-small">{te(job.error)}</p>}
        <div className="frz-card-actions">
          <Link to={`/studio/${p.id}`} className="frz-btn frz-btn-ghost frz-btn-sm"><Pencil size={14} /> {t("open")}</Link>
          {cur && <a href={fileUrl(cur, true)} className="frz-btn frz-btn-ghost frz-btn-sm" data-testid={`creation-download-${p.id}`}><Download size={14} /> {t("download")}</a>}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button type="button" className="frz-btn frz-btn-danger frz-btn-sm" data-testid={`creation-delete-${p.id}`}><Trash2 size={14} /></button>
            </AlertDialogTrigger>
            <AlertDialogContent className="frz-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>{t("confirm_delete")}</AlertDialogTitle>
                <AlertDialogDescription>{t("confirm_delete_d")}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-cancel-button">{t("cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={() => onDelete(p.id)} data-testid="delete-confirm-button">{t("delete")}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
        {p.exports.length > 0 && (
          <div className="frz-card-exports">
            {p.exports.slice(-3).map((e) => (
              <a key={e.created_at} href={fileUrl(e.file, true)} className="frz-link frz-small">{t(`fmt_${e.format}`)}</a>
            ))}
          </div>
        )}
      </div>
    </article>
  );
};

export default function Creations() {
  const { t, te } = useLang();
  const [items, setItems] = useState(null);
  const load = useCallback(async () => {
    try { setItems(await api.projects()); } catch (e) { toast.error(te(errDetail(e))); }
  }, [te]);
  useEffect(() => { load(); }, [load]);
  const active = items?.some((p) => p.jobs.some((j) => ACTIVE.includes(j.status)));
  useEffect(() => {
    if (!active) return undefined;
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [active, load]);
  const onDelete = async (id) => {
    try { await api.remove(id); toast.success(t("deleted")); load(); } catch (e) { toast.error(te(errDetail(e))); }
  };
  return (
    <div className="frz-page">
      <HeaderNav />
      <main className="frz-section" data-testid="creations-page">
        <div className="frz-section-head">
          <h1 className="frz-h1">{t("creations_title")}</h1>
          <p className="frz-muted">{t("creations_sub")}</p>
        </div>
        {items && items.length === 0 && <p className="frz-muted" data-testid="creations-empty">{t("creations_empty")}</p>}
        <div className="frz-cards" data-testid="creations-grid">
          {items?.map((p) => <CreationCard key={p.id} p={p} onDelete={onDelete} />)}
        </div>
      </main>
    </div>
  );
}
